import { Component, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EventOriginEnum, NavigateToEnum } from '@app/core/enums/analytics/analytics-value.enum';
import { EntityTypeEnum } from '@app/core/enums/entity-type.enum';
import { PermissionEnum } from '@app/core/enums/permissions.enum';
import { Contract, ContractInput } from '@app/core/model/entities/asset/contract';
import { Document } from '@app/core/model/entities/document/document';
import { ContractsService } from '@app/features/main/views/organization-contracts/contracts.service';
import { ContractModalService } from '@app/features/main/views/organization-contracts/modal/contract-modal.service';
import { DocumentModalService } from '@app/features/main/views/organization-documents/modals/document-modal.service';
import { FormStateService } from '@app/shared/components/form-builder/form-state.service';
import { AbstractSidebar } from '@app/shared/components/side-panel/abstract-sidebar';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';

@Component({
    selector: 'contract-sidebar',
    templateUrl: './contract-sidebar.component.html',
    providers: [ContractModalService, DocumentModalService, FormStateService],
    standalone: false
})
export class ContractSidebarComponent extends AbstractSidebar<Contract> {

  public permissionsForEdition: string[] = [PermissionEnum.EDIT_CONTRACT];

  // Injection
  protected readonly translate = inject(TranslateService);
  private readonly contractsService = inject(ContractsService);
  private readonly contractModalService = inject(ContractModalService);

  constructor() {
    super();

    this.entitiesUpdated$ = this.contractsService.contractUpdated$;
    this.sidePanelToggle$ = this.contractsService.sidePanelToggle$
      .pipe(
        filter(contractData => !!contractData),
        switchMap(contractData => {
          return this.fieldService.getField('provider', EntityTypeEnum.CONTRACT)
            .pipe(
              map(field => {
                this.validators = field.validators;
                return contractData;
              })
            );
        }),
        takeUntilDestroyed(this.destroyRef)
      );
    this.afterLoadRequests.push(this.loadContractDocuments.bind(this));
  }

  /**
   * Loads the Contract Documents.
   * @param contract Contract to load the Documents for.
   * @return Observable<Document[]>.
   */
  private loadContractDocuments(contract: Contract): Observable<Document[]> {
    return this.contractsService.loadContractDocuments(contract.id)
      .pipe(
        tap(documents => this.entity.documents = documents),
        takeUntilDestroyed(this.destroyRef)
      );
  }

  /**
   * API call to update the Contract.
   * @param data The update input.
   * @returns The updated Contract.
   */
  protected override updateEntity(data: Record<string, any>): Observable<Contract> {
      Contract.transformPropertiesForInput(data);
      return this.contractsService.updateContract(this.entity, data as ContractInput)
        .pipe(
          filter(updatedContract => updatedContract.id === this.entity.id),
          tap(updatedContract => {
            updatedContract.documents = this.entity.documents;
            this.entity = updatedContract;
          })
        );
  }

  /**
   * Close the side panel.
   */
  public override closeSidePanel(): void {
    this.contractsService.closeContractSidePanel();
  }

  /**
   * Open a dialog for the User to renew the Contract.
   */
  public renewContract(): void {
    this.analyticsService.trackNavigationEvent(
      this.eventsOrigin,
      NavigateToEnum.RENEW_DIALOG,
      EntityTypeEnum.CONTRACT,
      this.entity.id
    );
    this.contractModalService.openRenewContractDialog(this.entity)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.snackbarManager.showActionSnackbar(this.translate.instant('SUCCESS.CONTRACT_RENEWED')));
  }

  /**
   * Open a confirmation dialog prior to deleting the Contract.
   */
  public deleteContract(): void {
    this.analyticsService.trackNavigationEvent(
      this.eventsOrigin,
      NavigateToEnum.DELETE_DIALOG,
      EntityTypeEnum.CONTRACT,
      this.entity.id
    );
    this.contractModalService.openDeleteContractsDialog([this.entity])
      .pipe(
        filter(success => success),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.snackbarManager.showActionSnackbar(this.translate.instant('SUCCESS.CONTRACT_DELETED')));
  }

  /**
   * Display a dialog for the user to upload Documents linked to a Contract.
   */
  public uploadContractDocuments(): void {
    this.analyticsService.trackNavigationEvent(
      EventOriginEnum.SIDEPANEL,
      NavigateToEnum.UPLOAD_DIALOG,
      EntityTypeEnum.CONTRACT,
      this.entity.id
    );
    this.contractModalService.openUploadContractDocumentsDialog(this.entity.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((documents) => {
        this.entity.documents.push(...documents);
        this.snackbarManager.showActionSnackbar(this.translate.instant('SUCCESS.DOCUMENT_UPLOADED'));
      });
  }

  /**
   * Display a confirmation dialog before deleting a Contract's Document.
   * @param document Document to delete.
   */
  public deleteContractDocument(document: Document): void {
    this.analyticsService.trackNavigationEvent(
      this.eventsOrigin,
      NavigateToEnum.DELETE_DIALOG,
      EntityTypeEnum.DOCUMENT,
      document.id
    );
    this.contractModalService.openDeleteContractDocumentDialog(document)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(documentIsDeleted => {
        if (documentIsDeleted) {
          this.entity.documents = this.entity.documents.filter(doc => doc.id !== document.id);
        }
      });
  }

  /**
   * Navigate to the Contract's related Asset sheet's contracts tab.
   */
  public async navigateToAssetSheet(): Promise<void> {
    this.closeSidePanel();
    this.analyticsService.trackNavigationEvent(
      this.eventsOrigin,
      NavigateToEnum.SHEET,
      EntityTypeEnum.ASSET,
      this.entity.assetId
    );
    await this.contractsService.navigateToAssetSheet(this.entity.assetId);
  }
}
